/**
 * Idle logout for staff sessions.
 *
 * While the user moves the mouse, types or touches the screen the browser sends a throttled
 * heartbeat, which keeps the server-side session open; once they have done none of that for
 * the configured number of minutes the browser logs them out.
 */

export const SESSION_IDLE_TIMEOUT_CONFIG_KEY = 'session_idle_timeout_minutes';
export const DEFAULT_SESSION_IDLE_TIMEOUT_MINUTES = 30;

/** The choices offered on the Configuration page; the backend accepts 5 to 480. */
export const SESSION_IDLE_TIMEOUT_OPTIONS: readonly number[] = [15, 30, 60, 120, 240, 480];

/** How often the browser compares the last activity with the idle timeout. */
export const SESSION_ACTIVITY_CHECK_INTERVAL_MS = 15_000;

/**
 * The least time between two heartbeats. The server keeps a session 5 minutes longer than
 * the idle timeout, so a heartbeat that lags activity by this much never lets it lapse early.
 */
export const SESSION_HEARTBEAT_MIN_INTERVAL_MS = 60_000;

/** Shared between tabs, so working in one tab keeps the others logged in as well. */
export const SESSION_LAST_ACTIVITY_STORAGE_KEY = 'sessionLastActivityAt';

export const SESSION_ACTIVITY_EVENTS: readonly string[] = [
    'mousemove',
    'mousedown',
    'wheel',
    'keydown',
    'touchstart',
];

const MILLISECONDS_PER_MINUTE = 60_000;
const MINUTES_PER_HOUR = 60;

/** A positive whole number of minutes from a response or stored value, or the default. */
export function resolveSessionIdleTimeoutMinutes(value: unknown): number {
    const minutes = typeof value === 'string' ? Number(value.trim()) : value;

    if (typeof minutes === 'number' && Number.isInteger(minutes) && minutes > 0) {
        return minutes;
    }

    return DEFAULT_SESSION_IDLE_TIMEOUT_MINUTES;
}

export type SessionAction = 'logout' | 'heartbeat' | 'none';

export interface SessionActivityState {
    now: number;
    lastActivityAt: number;
    lastHeartbeatAt: number;
    idleTimeoutMinutes: number;
}

export function decideSessionAction({
    now,
    lastActivityAt,
    lastHeartbeatAt,
    idleTimeoutMinutes,
}: SessionActivityState): SessionAction {
    if (now - lastActivityAt >= idleTimeoutMinutes * MILLISECONDS_PER_MINUTE) {
        return 'logout';
    }

    const activeSinceHeartbeat = lastActivityAt > lastHeartbeatAt;
    const heartbeatDue = now - lastHeartbeatAt >= SESSION_HEARTBEAT_MIN_INTERVAL_MS;

    return activeSinceHeartbeat && heartbeatDue ? 'heartbeat' : 'none';
}

/** The later of this tab's last activity and the one another tab stored. */
export function latestActivityAt(localActivityAt: number, storedValue: string | null): number {
    const storedActivityAt = Number(storedValue);

    return Number.isFinite(storedActivityAt)
        ? Math.max(localActivityAt, storedActivityAt)
        : localActivityAt;
}

export interface IdleTimeoutLabel {
    unit: 'minutes' | 'hours';
    count: number;
}

/** "90" reads as 90 minutes, "120" as 2 hours. */
export function describeIdleTimeout(minutes: number): IdleTimeoutLabel {
    if (minutes >= MINUTES_PER_HOUR && minutes % MINUTES_PER_HOUR === 0) {
        return { unit: 'hours', count: minutes / MINUTES_PER_HOUR };
    }

    return { unit: 'minutes', count: minutes };
}

/**
 * The options for the timeout selector. A saved value that is not one of the standard
 * choices (set directly in the database, say) is kept in the list, so the selector shows it
 * instead of a bare number.
 */
export function idleTimeoutOptions(currentMinutes: number): number[] {
    if (SESSION_IDLE_TIMEOUT_OPTIONS.includes(currentMinutes)) {
        return [...SESSION_IDLE_TIMEOUT_OPTIONS];
    }

    return [...SESSION_IDLE_TIMEOUT_OPTIONS, currentMinutes].sort(
        (first, second) => first - second,
    );
}
